/**
 * AppShell — the redesign application frame, shared by the Portfolio surface and
 * every property workspace page.
 *
 * Composes AppSidebar + AppTopbar + a scrollable content region, and mounts the
 * cross-cutting concerns that previously lived in PropertyWorkspaceLayout:
 * trial / demo banners, the trial-expired modal, and the FRAME concierge
 * (trigger + panel + proactive teaser + ⌘K), all wired to useConcierge.
 *
 * mode="portfolio" → propertyId null → concierge runs at portfolio altitude.
 * mode="property"  → propertyId from the route.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Outlet } from "react-router"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { AppSidebar } from "./AppSidebar"
import { AppTopbar, type Crumb } from "./AppTopbar"
import { TrialUrgencyBanner } from "@/components/trial/TrialUrgencyBanner"
import { TrialExpiredModal } from "@/components/trial/TrialExpiredModal"
import { DemoBanner } from "@/components/demo/DemoBanner"
import {
  ConciergeTrigger,
  ConciergePanel,
  ConciergeProactiveTeaser,
  useConcierge,
} from "@/components/concierge"

interface AppShellProps {
  mode: "portfolio" | "property"
  propertyId?: string | null
  title?: string
  subtitle?: string
  breadcrumb?: Crumb[]
  children?: ReactNode
}

export function AppShell({ mode, propertyId, title, subtitle, breadcrumb, children }: AppShellProps) {
  const { isDemo } = usePropertyContext()
  // Demo chrome only applies at property altitude — usePropertyContext falls back
  // to the stored active property at portfolio altitude, which must not leak here.
  const demoActive = mode === "property" && isDemo
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const {
    messages,
    isLoading,
    proactiveMessage,
    isPanelOpen,
    sendMessage,
    openPanel,
    closePanel,
    dismissProactive,
    openProactive,
  } = useConcierge(propertyId ?? undefined)

  // ⌘K toggles the concierge panel (also the topbar Search affordance).
  const handleGlobalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        if (isPanelOpen) closePanel()
        else openPanel()
      }
    },
    [isPanelOpen, openPanel, closePanel],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleGlobalKeyDown)
    return () => document.removeEventListener("keydown", handleGlobalKeyDown)
  }, [handleGlobalKeyDown])

  // Close the mobile drawer whenever the surface changes.
  useEffect(() => {
    setSidebarOpen(false)
  }, [propertyId, mode])

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <AppSidebar mode={mode} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        <AppTopbar
          title={title}
          subtitle={subtitle}
          breadcrumb={breadcrumb}
          hasNotification={!!proactiveMessage}
          onAsk={openPanel}
          onSearch={openPanel}
          onBell={openPanel}
          onMenuClick={() => setSidebarOpen(true)}
        />
        <div className="flex-1 overflow-y-auto">
          {demoActive ? <DemoBanner /> : <TrialUrgencyBanner />}
          {children ?? <Outlet />}
        </div>
      </main>

      <TrialExpiredModal />

      {/* FRAME concierge */}
      <ConciergeTrigger
        hasNotification={!!proactiveMessage}
        priority={proactiveMessage?.priority}
        isPanelOpen={isPanelOpen}
        onClick={() => (isPanelOpen ? closePanel() : openPanel())}
      />

      {proactiveMessage && !isPanelOpen && proactiveMessage.priority !== "low" && (
        <ConciergeProactiveTeaser message={proactiveMessage} onDismiss={dismissProactive} onOpen={openProactive} />
      )}

      {isPanelOpen && (
        <div className="fixed inset-0 z-[199] bg-black/15 transition-opacity duration-300" onClick={closePanel} />
      )}

      <ConciergePanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        messages={messages}
        onSend={sendMessage}
        onQuickAction={sendMessage}
        isLoading={isLoading}
      />
    </div>
  )
}
